/// <reference types="vite-plugin-pwa/react" />

import { useRegisterSW } from 'virtual:pwa-register/react'
import { Button, Group, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import type { DataRouter } from 'react-router'
import type { WorkerMessage } from './messages'

const HOUR = 60 * 60_000

// Nothing pressed or typed since the page loaded: a reload loses nothing
let touched = false
const touch = () => {
  touched = true
}
window.addEventListener('pointerdown', touch, { capture: true, once: true })
window.addEventListener('keydown', touch, { capture: true, once: true })

/**
 * Registers the service worker and brings a new version in by itself when a reload loses nothing: as the app
 * opens, before the person has touched it, or as they move to another page (a form with unsaved changes asks
 * before they leave it: ResourceForm). Until then a notice offers to reload now.
 */
export function AppUpdate({ router }: { router: DataRouter }) {
  const { t } = useTranslation()
  // Another tab of the app brought the new version in: this one reloads at its own safe moment, not at once
  const [takenOver, setTakenOver] = useState(false)
  const asked = useRef(false)
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW: (_url, registration) => {
      if (!registration) return
      // Office tabs stay open all day, phones switch apps all day: look every hour and each time the app is back on screen
      setInterval(() => registration.update(), HOUR)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') registration.update()
      })
    },
    onNeedReload: () => (asked.current ? window.location.reload() : setTakenOver(true)),
  })
  const update = useCallback(() => {
    if (takenOver) return window.location.reload()
    asked.current = true
    updateServiceWorker()
  }, [takenOver, updateServiceWorker])

  useEffect(() => {
    if (!needRefresh && !takenOver) return
    if (!touched) {
      update()
      return
    }
    let path = router.state.location.pathname
    const stopWatching = router.subscribe(({ location }) => {
      if (location.pathname === path) return
      path = location.pathname
      update()
    })
    notifications.show({
      id: 'app-update',
      color: 'info',
      autoClose: false,
      message: (
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text size="sm">{t('pwa.updateReady')}</Text>
          <Button variant="subtle" size="compact-sm" onClick={update}>
            {t('pwa.reload')}
          </Button>
        </Group>
      ),
    })
    return () => {
      stopWatching()
      notifications.hide('app-update')
    }
  }, [needRefresh, takenOver, update, router, t])
  return null
}

/** Messages from the service worker: a notification arrived, or one was tapped while the app was open */
export function listenToWorker(handlers: { push: () => void; open: (path: string) => void }) {
  navigator.serviceWorker?.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
    if (event.data?.type === 'push') handlers.push()
    if (event.data?.type === 'open') handlers.open(event.data.path)
  })
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<unknown>
}

// Chrome and Edge offer to install the app through this event, often before React starts: keep it
let installEvent: InstallPromptEvent | null = null
const installListeners = new Set<() => void>()
const setInstallEvent = (event: InstallPromptEvent | null) => {
  installEvent = event
  for (const listener of installListeners) listener()
}
window.addEventListener('beforeinstallprompt', (event) => setInstallEvent(event as InstallPromptEvent))
window.addEventListener('appinstalled', () => setInstallEvent(null))
const subscribeInstall = (listener: () => void) => {
  installListeners.add(listener)
  return () => installListeners.delete(listener)
}

/** Opens the browser's install dialog; undefined when the browser offers none (installed already, Safari, Firefox) */
export function useInstallApp(): (() => void) | undefined {
  const event = useSyncExternalStore(subscribeInstall, () => installEvent)
  if (!event) return undefined
  return () => {
    // The event works once
    setInstallEvent(null)
    event.prompt().catch(() => undefined)
  }
}
